import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import {
  collection, onSnapshot, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { ShortNoPill } from '../components/IdPill';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { shortInvoiceNo } from '../utils/invoiceNo';
import { useSalePopup } from '../components/SalePopup';
import { findReturnHistoryRefs } from '../utils/returnHistory';
import ReturnHistoryCleanup from '../components/ReturnHistoryCleanup';
import { Button, Modal, ConfirmModal, FormField, Dropdown, DeleteIconButton } from '../components/ui';
import SalesReturnModal from '../components/SalesReturnModal';
import { HeaderSearch, DateRangeButton, StatChips, IconPlusSm, IconExportSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { downloadCSV } from '../utils/csv';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import { entryId } from '../utils/ledger';
import type { SalesReturn, SaleInvoice, Entity, InventoryItem, PaymentStatus } from '../types';
import { returnCreditOf } from '../utils/tradingSystem';

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
// Invoice No is stored in full, but shown as just the prefix + last 4 digits
// to keep the pill compact — the full value is always in a `title` tooltip.
function lotNoDisplay(invoiceNo: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> credit note{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

function SalePickPayBadge({ inv }: { inv: SaleInvoice }) {
  const status = inv.paymentStatus ?? 'unpaid';
  const paid   = inv.paidUSD ?? 0;
  const pct    = inv.totalAmountUSD > 0 ? Math.round((paid / inv.totalAmountUSD) * 100) : 0;
  if (status === 'paid')    return <span className="badge" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)', fontSize: 10, flexShrink: 0 }}>Paid</span>;
  if (status === 'partial') return <span className="badge" style={{ background: 'var(--warning-bg)', color: '#c2410c', border: '1px solid var(--warning)', fontSize: 10, flexShrink: 0 }}>{pct}% paid</span>;
  return <span className="badge" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)', fontSize: 10, flexShrink: 0 }}>Unpaid</span>;
}

function PickSaleModal({ sales, entityName, money, onPick, onClose }: {
  sales: SaleInvoice[]; entityName: (id: string) => string; money: (v: number) => string;
  onPick: (s: SaleInvoice) => void; onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const rows = sales
    .filter(s => !needle || s.invoiceNo.toLowerCase().includes(needle) || entityName(s.toEntityId).toLowerCase().includes(needle))
    .sort((a, b) => (b.saleDate ?? '').localeCompare(a.saleDate ?? ''))
    .slice(0, 40);
  const th: CSSProperties = {
    position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface-2)',
    padding: '8px 12px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)',
    textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left',
    borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
  };
  return (
    <Modal open onClose={onClose} title="Credit note against which sale?" size="2xl"
      footer={<Button variant="secondary" onClick={onClose}>Cancel</Button>}>
      <FormField>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search invoice no or customer…" autoFocus />
      </FormField>
      <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={th}>Invoice No</th>
              <th style={th}>Date</th>
              <th style={th}>Customer</th>
              <th style={{ ...th, textAlign: 'right' }}>Items</th>
              <th style={{ ...th, textAlign: 'right' }}>Carat</th>
              <th style={{ ...th, textAlign: 'right' }}>Amount</th>
              <th style={th}>Payment</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 20, fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>No sales found.</td></tr>
            ) : rows.map((s, i) => {
              const carat = s.lineItems.reduce((sum, li) => sum + (li.carat ?? 0), 0);
              return (
                <tr key={s.id} onClick={() => onPick(s)}
                  style={{ cursor: 'pointer', background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                    <span title={s.invoiceNo} style={{
                      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
                      background: 'var(--primary-light)', color: 'var(--primary)', padding: '3px 8px', borderRadius: 6,
                    }}>{lotNoDisplay(s.invoiceNo)}</span>
                  </td>
                  <td style={{ padding: '9px 12px', fontFamily: 'var(--font-num)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{fmtDate(s.saleDate)}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: 'var(--text-1)' }}>{entityName(s.toEntityId)}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{s.lineItems.length}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{carat.toFixed(2)}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 600, color: 'var(--text-1)' }}>{money(s.totalAmountUSD)}</td>
                  <td style={{ padding: '9px 12px' }}><SalePickPayBadge inv={s} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export default function SalesReturnsPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [returns, setReturns] = useState<SalesReturn[]>([]);
  const [sales, setSales]     = useState<SaleInvoice[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  const [searchQ, setSearchQ] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [picking, setPicking] = useState(false);
  const [returnFor, setReturnFor] = useState<SaleInvoice | null>(null);
  const [confirmDel, setConfirmDel] = useState<SalesReturn | null>(null);
  const [delError, setDelError] = useState('');
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'salesReturns'),
      snap => { setReturns(snap.docs.map(d => ({ id: d.id, ...d.data() } as SalesReturn))); setLoading(false); },
      () => setLoading(false));
    const u2 = onSnapshot(collection(db, 'sales'),
      snap => setSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))), () => {});
    const u3 = onSnapshot(collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))), () => {});
    return () => { u1(); u2(); u3(); };
  }, [business]);

  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  // The Sale number opens that sale (Payments tab shows this credit note in its timeline).
  const salePopup = useSalePopup(entityName);
  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const baseRows = useMemo(() => {
    if (!window) return [];
    const q = searchQ.trim().toLowerCase();
    return returns
      .filter(r => inWindow(r.date, window))
      .filter(r => !q || [r.returnNo, r.saleInvoiceNo, entityName(r.toEntityId)].some(v => (v ?? '').toLowerCase().includes(q)))
      .sort((a, b) => b.date.localeCompare(a.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returns, window, searchQ, entities]);
  // Excel-style filters on Date (by month), Note No, Customer and Sale.
  const filterColumns = useMemo<FilterColumn<SalesReturn>[]>(() => [
    { key: 'month',    label: 'Date',     get: r => r.date ? new Date(r.date + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '',
      sort: (a, b) => new Date('1 ' + a).getTime() - new Date('1 ' + b).getTime() },
    { key: 'note',     label: 'Note No',  get: r => shortInvoiceNo(r.returnNo) },
    { key: 'customer', label: 'Customer', get: r => entityName(r.toEntityId) },
    { key: 'sale',     label: 'Sale',     get: r => shortInvoiceNo(r.saleInvoiceNo) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows, filters: colFilters } = useColumnFilters(baseRows, filterColumns);
  const total = useMemo(() => rows.reduce((s, r) => s + returnCreditOf(r.totalRevenueUSD, r), 0), [rows]);

  useEffect(() => { setCurrentPage(1); }, [dateKey, customFrom, customTo, searchQ, colFilters.stamp]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Print covers every filtered row, not just this page (same as the other list pages).
  // globalThis, not window: `window` here is the page's date range.
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    globalThis.addEventListener('afterprint', after);
    requestAnimationFrame(() => { if (!cancelled) requestAnimationFrame(() => { if (!cancelled) globalThis.print(); }); });
    return () => { cancelled = true; globalThis.removeEventListener('afterprint', after); };
  }, [printAll]);

  function handleExportCSV() {
    downloadCSV('sales-returns',
      ['Date', 'Note No', 'Customer', 'Sale', 'Carat', 'Credit'],
      rows.map(r => [r.date, r.returnNo, entityName(r.toEntityId), r.saleInvoiceNo ?? '', (r.totalCarat ?? 0).toFixed(2), returnCreditOf(r.totalRevenueUSD, r).toFixed(2)]),
    );
  }

  async function doDelete(ret: SalesReturn) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      const saleRef = doc(db, 'sales', ret.saleId);
      // The "Returned by customer" lines it wrote in the stones' history.
      const histRefs = await findReturnHistoryRefs(db, 'sale', ret);
      await runTransaction(db, async tx => {
        const itemSnaps = await Promise.all(ret.lines.map(l => tx.get(doc(db, 'inventory', l.inventoryItemId))));
        const saleSnap = await tx.get(saleRef);
        // Parcels a return put carats back into (see SalesReturnModal).
        const parentIds = [...new Set(ret.lines.map(l => l.mergedIntoId).filter((x): x is string => !!x))];
        const parentSnaps = await Promise.all(parentIds.map(id => tx.get(doc(db, 'inventory', id))));
        const parentTake = new Map<string, { carat: number; cost: number; costing: number }>();

        ret.lines.forEach((l, i) => {
          const s = itemSnaps[i];
          const itRef = doc(db, 'inventory', l.inventoryItemId);
          if (l.mergedIntoId) {
            // Take the carats back out of the parcel and give them back to the sold piece.
            const acc = parentTake.get(l.mergedIntoId) ?? { carat: 0, cost: 0, costing: 0 };
            acc.carat += l.carat; acc.cost += l.costUSD; acc.costing += l.costingUSD;
            parentTake.set(l.mergedIntoId, acc);
            const cur = s.exists() ? (s.data() as InventoryItem) : null;
            tx.update(itRef, stripUndef({
              carat: round2((cur?.carat ?? 0) + l.carat),
              costUSD: round2((cur?.costUSD ?? 0) + (l.pieceCostUSD ?? l.costUSD)),
              costingUSD: round2((cur?.costingUSD ?? 0) + (l.pieceCostingUSD ?? l.costingUSD)),
              soldAmountUSD: l.soldAmountBefore ?? round2((cur?.soldAmountUSD ?? 0) + l.revenueUSD),
              status: 'sold',
              soldToEntityId: ret.toEntityId, soldDate: ret.saleDate,
              updatedAt: serverTimestamp(),
            }));
          } else if (l.wholeItem) {
            // it was flipped back to in-stock — mark it sold again
            tx.update(itRef, stripUndef({
              status: 'sold',
              soldToEntityId: ret.toEntityId, soldDate: ret.saleDate, soldAmountUSD: l.revenueUSD,
              updatedAt: serverTimestamp(),
            }));
          } else {
            if (l.sliceId) tx.delete(doc(db, 'inventory', l.sliceId));
            if (s.exists()) {
              const cur = s.data() as InventoryItem;
              tx.update(itRef, {
                carat: round2((cur.carat ?? 0) + l.carat),
                costUSD: round2((cur.costUSD ?? 0) + l.costUSD),
                costingUSD: round2((cur.costingUSD ?? 0) + l.costingUSD),
                soldAmountUSD: round2((cur.soldAmountUSD ?? 0) + l.revenueUSD),
                updatedAt: serverTimestamp(),
              });
            }
          }
        });

        parentSnaps.forEach(ps => {
          const take = parentTake.get(ps.id);
          if (!ps.exists() || !take) return;
          const par = ps.data() as InventoryItem;
          if ((par.carat ?? 0) < take.carat - 1e-6) {
            throw new Error(`${par.code || par.shape}: the returned ${take.carat.toFixed(2)} ct is no longer all in stock (some was sold or moved since). It can't be undone.`);
          }
          tx.update(doc(db, 'inventory', ps.id), {
            carat: round2((par.carat ?? 0) - take.carat),
            costUSD: round2((par.costUSD ?? 0) - take.cost),
            costingUSD: round2((par.costingUSD ?? 0) - take.costing),
            updatedAt: serverTimestamp(),
          });
        });

        if (saleSnap.exists()) {
          const sale = saleSnap.data() as SaleInvoice;
          const newCredited = round2(Math.max(0, (sale.creditedUSD ?? 0) - returnCreditOf(ret.totalRevenueUSD, ret)));
          const settled = (sale.paidUSD ?? 0) + newCredited;
          const newStatus: PaymentStatus =
            settled >= sale.totalAmountUSD - 0.005 ? 'paid' : settled > 0 ? 'partial' : 'unpaid';
          tx.update(saleRef, { creditedUSD: newCredited, paymentStatus: newStatus, updatedAt: serverTimestamp() });
        }

        tx.delete(doc(db, 'salesReturns', ret.id));
        histRefs.forEach(r => tx.delete(r));
        tx.delete(doc(db, 'journalEntries', entryId('sales_return', ret.id)));
      });
      setConfirmDel(null);
    } catch (e) {
      // Keep the confirm open with the reason (e.g. the carats were sold again since).
      setDelError(e instanceof Error ? e.message : 'Could not delete this credit note.');
    } finally { setBusy(false); }
  }

  const customerName = returnFor ? entityName(returnFor.toEntityId) : '';

  return (
    <AppLayout
      title="Sales Returns"
      subtitle="Goods a customer sent back — restocks inventory and credits their account."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Credit note, invoice no, customer…" label="Search credit notes" />
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        {(searchQ || dateKey !== 'this-month' || colFilters.active.length > 0) && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); colFilters.clear(); }}>Clear</button>
        )}
      </>}
      headerActions={<>
        <div className="tb-group">
          <Dropdown
            align="right"
            trigger={() => (
              <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExportSm /></button>
            )}
          >
            <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
          </Dropdown>
        </div>
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setPicking(true)} disabled={!business}
          style={{ height: 34, boxSizing: 'border-box' }}>New Credit Note</Button>
      </>}
    >
      <ReturnHistoryCleanup kind="sale" returns={returns} loading={loading} />
      <StatChips items={[
        { label: 'Credited', value: money(total), tone: 'primary' },
        { label: 'Credit notes', value: String(rows.length) },
      ]} end={colFilters.active.length > 0 ? <ColumnFilterChips filters={colFilters} inline /> : undefined} />

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : baseRows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No credit notes here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Use "New Credit Note" when a customer returns goods.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {/* Fixed widths + an empty spacer column that takes the slack, so
                      each filter funnel sits right beside its heading. */}
                  <th className="th-filterable" style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Date<ColumnFilterButton filters={colFilters} colKey="month" label="Date" /></th>
                  <th className="th-filterable" style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Note No<ColumnFilterButton filters={colFilters} colKey="note" label="Note No" /></th>
                  <th className="th-filterable" style={{ width: 240, minWidth: 240, maxWidth: 240 }}>Customer<ColumnFilterButton filters={colFilters} colKey="customer" label="Customer" /></th>
                  <th className="th-filterable" style={{ width: 130, minWidth: 130, maxWidth: 130 }}>Sale<ColumnFilterButton filters={colFilters} colKey="sale" label="Sale" /></th>
                  <th style={{ textAlign: 'right', width: 100, minWidth: 100, maxWidth: 100 }}>Carat</th>
                  <th style={{ textAlign: 'right', width: 130, minWidth: 130, maxWidth: 130 }}>Credit</th>
                  <th aria-hidden="true" />
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(printAll ? rows : pagedRows).map(r => (
                  <tr key={r.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(r.date)}</td>
                    <td><ShortNoPill value={r.returnNo} /></td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', maxWidth: 240 }}><span className="cell-clip" title={entityName(r.toEntityId)}>{entityName(r.toEntityId)}</span></td>
                    <td>
                      <button type="button" className="sm-link" title="Open this sale" onClick={() => salePopup.open(r.saleId, { tab: 'payments' })}>
                        <ShortNoPill value={r.saleInvoiceNo} />
                      </button>
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{(r.totalCarat ?? 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', fontWeight: 600 }}>{money(returnCreditOf(r.totalRevenueUSD, r))}</td>
                    <td aria-hidden="true" />
                    <td className="col-actions">
                      <DeleteIconButton onClick={() => setConfirmDel(r)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      {picking && (
        <PickSaleModal
          sales={sales} entityName={entityName} money={money}
          onPick={s => { setPicking(false); setReturnFor(s); }}
          onClose={() => setPicking(false)}
        />
      )}
      {returnFor && (
        <SalesReturnModal sale={returnFor} customerName={customerName} onClose={() => setReturnFor(null)} />
      )}
      {confirmDel && (
        <ConfirmModal
          open title="Delete this credit note?"
          message={`${confirmDel.returnNo} will be undone: ${(confirmDel.totalCarat ?? 0).toFixed(2)} ct is marked sold again and ${money(returnCreditOf(confirmDel.totalRevenueUSD, confirmDel))} is added back to what the customer owes.`}
          confirmLabel="Delete" loading={busy} error={delError}
          onConfirm={() => { setDelError(''); doDelete(confirmDel); }} onClose={() => { setConfirmDel(null); setDelError(''); }}
        />
      )}
      {salePopup.popup}
    </AppLayout>
  );
}

function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
